import axios from 'axios';
import Cookies from 'js-cookie';
import { GRAPHQL_HTTP_URL } from '../config/api';
import { reportAccessError } from './subscriptionAccess';

/**
 * Makes a GraphQL request to the backend API
 * @param {string} query - GraphQL query or mutation string
 * @param {object} variables - Variables for the query/mutation
 * @returns {Promise<object>} - GraphQL response data
 * @throws {Error} - If the request fails or contains GraphQL errors
 */
export const graphqlRequest = async (query, variables = {}, options = {}) => {
  const timeout = options.timeout ?? 30000;
  const token = Cookies.get('accessToken');
  
  if (!token) {
    console.warn('⚠️ No accessToken found in cookies. Request may fail if authentication is required.');
  }

  try {
    const graphqlEndpoint = GRAPHQL_HTTP_URL;
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Only add Authorization header if token exists
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

  const response = await axios.post(
      graphqlEndpoint,
    { query, variables },
      { headers, timeout },
    );

    // Check for GraphQL errors in response
    if (response.data.errors) {
      console.error('❌ GraphQL Errors:', response.data.errors);
      const errorMessage = response.data.errors[0]?.message || 'GraphQL request failed';
      // Subscription ended / account blocked → App switches to billing-only or logs out.
      reportAccessError(errorMessage);

      // Check for authentication errors
      if (errorMessage.includes('Unauthorized') || errorMessage.includes('authorized') || 
          errorMessage.includes('token') || errorMessage.includes('authentication')) {
        console.error('🔒 Authentication error detected. Please check if you are logged in.');
        // Could redirect to login page here if needed
      }
      
      throw new Error(errorMessage);
    }

  return response.data.data;
  } catch (error) {
    console.error('GraphQL Request Error:', error);
    
    // Handle network errors
    if (error.response) {
      // Server responded with error status
      const statusCode = error.response.status;
      const errorMessage = error.response.data?.errors?.[0]?.message || 
                          error.response.data?.message || 
                          error.message;
      
      // Handle 401 Unauthorized specifically (token invalid/expired).
      // Do NOT clear token on 403: that's an authorization issue and would
      // cause the UI to "blank out" by logging the user out mid-navigation.
      if (statusCode === 401) {
        console.error('🔒 Unauthorized: Please log in again. Status:', statusCode);
        Cookies.remove('accessToken');
      }
      reportAccessError(errorMessage);
      
      throw new Error(errorMessage);
    } else if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
      throw new Error(
        'The request timed out. Check that the backend is running and try again.',
      );
    } else if (error.request) {
      // Request made but no response received
      throw new Error('No response from server. Please check if the backend is running.');
    } else {
      // Error setting up request
      throw new Error(error.message || 'Failed to make GraphQL request');
    }
  }
};
